import {
  api,
  virtualPowerGrantInput,
  type VirtualPowerGrant,
  type VirtualPowerGrantInput,
} from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { Alert, Button, Card, Chip, FormGroup, Input, Select } from '../../ui';
import { formatDateTime } from '../../lib/format';

const col = createColumnHelper<VirtualPowerGrant>();

/**
 * Admin grants virtual left/right binary leg units to a member by their User
 * ID. The grant behaves exactly like a real active downline member: it
 * generates matching/achievement income and cascades up the member's entire
 * upline — see BinaryCounterService + VirtualPowerAggregator server-side for
 * how that cascade is computed. This page only ever creates new grants; there
 * is no edit — a correction is a fresh, separately audited grant.
 */
export function VirtualPower() {
  const queryClient = useQueryClient();

  const form = useForm<VirtualPowerGrantInput>({
    resolver: zodResolver(virtualPowerGrantInput),
    defaultValues: { user_code: '', position: 'left', units: 1, note: '' },
  });

  const submit = useMutation({
    mutationFn: (values: VirtualPowerGrantInput) => api.admin.grantVirtualPower(values),
    onSuccess: () => {
      form.reset({ user_code: '', position: 'left', units: 1, note: '' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'virtual-power-history'] });
    },
  });

  const { query, setPage } = useTableQuery({ defaultSort: 'created_at' });
  const { data: history, isLoading: historyLoading, error: historyError } = useQuery({
    queryKey: ['admin', 'virtual-power-history', query],
    queryFn: () => api.admin.virtualPowerHistory(query),
  });

  const columns = useMemo(
    () => [
      col.accessor('created_at', { header: 'Date', cell: (i) => formatDateTime(i.getValue()) }),
      col.accessor('user_code', {
        header: 'Member',
        cell: (i) => `${i.getValue() ?? '—'}${i.row.original.user_name ? ` · ${i.row.original.user_name}` : ''}`,
      }),
      col.accessor('position', {
        header: 'Leg',
        cell: (i) => (
          <Chip tone={i.getValue() === 'left' ? 'info' : 'success'}>
            {i.getValue() === 'left' ? 'Left' : 'Right'}
          </Chip>
        ),
      }),
      col.accessor('units', { header: 'Units', cell: (i) => i.getValue() }),
      col.accessor('note', { header: 'Note', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('admin_name', { header: 'By admin', cell: (i) => i.getValue() ?? '—' }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Virtual Power"
        description="Grant a member extra left/right leg count. It counts toward matching income and Achievement Rewards, and cascades to their entire upline, exactly like a real recruited member."
      />

      <Card>
        {submit.isSuccess ? <Alert tone="success">Virtual power granted.</Alert> : null}
        {submit.error ? <Alert tone="danger">{(submit.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => submit.mutate(values))} noValidate>
          <FormGroup label="Member User ID" error={form.formState.errors.user_code?.message}>
            <Input placeholder="AO123456" {...form.register('user_code')} />
          </FormGroup>

          <FormGroup label="Leg" error={form.formState.errors.position?.message}>
            <Select {...form.register('position')}>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </Select>
          </FormGroup>

          <FormGroup label="Units" error={form.formState.errors.units?.message}>
            <Input type="number" step="1" min="1" {...form.register('units')} />
          </FormGroup>

          <FormGroup label="Note (optional)" error={form.formState.errors.note?.message}>
            <Input placeholder="Reason for this grant" {...form.register('note')} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={submit.isPending}>
            {submit.isPending ? 'Working…' : 'Grant Virtual Power'}
          </Button>
        </form>
      </Card>

      <DataTable<VirtualPowerGrant>
        title="Virtual Power Grant History"
        columns={columns}
        data={history?.data}
        meta={history?.meta}
        isLoading={historyLoading}
        error={historyError as Error | null}
        emptyLabel="No virtual power grants yet"
        onPageChange={setPage}
      />
    </>
  );
}
